import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Phone, Mail, MapPin, Star, Trophy, ExternalLink } from "lucide-react";

const TIER_COLOR = { gold: "#FFD700", silver: "#C0C0C0", bronze: "#CD7F32" };

export default function BusinessCardPage() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/cards/${slug}`).then((r) => setCard(r.data)).catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [slug]);

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(window.location.href)}`;

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500">Card not found</div>;
  if (!card) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  const tierColor = TIER_COLOR[card.tier.id] || TIER_COLOR.bronze;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #061224 0%, #0B3087 100%)" }} data-testid="business-card-page">
      <div className="max-w-md mx-auto px-4 py-10">
        <div className="flex justify-center mb-6"><Logo size={64} dark compact /></div>

        <div className="bg-white border-2 border-asap-yellow shadow-hard relative overflow-hidden" data-testid="business-card">
          <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ background: `repeating-linear-gradient(45deg, ${tierColor} 0 6px, transparent 6px 12px)` }}/>
          {/* Header */}
          <div className="relative p-6 pb-4" style={{ background: `linear-gradient(135deg, ${tierColor}, #FF6B00)` }}>
            <div className="flex items-center gap-4">
              {card.photo_url ? (
                <img src={card.photo_url} alt={card.name} className="w-20 h-20 rounded-full border-4 border-white object-cover"/>
              ) : (
                <div className="w-20 h-20 rounded-full border-4 border-white bg-asap-deep text-asap-yellow font-display text-3xl grid place-items-center">
                  {card.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-asap-deep">ASAP Roadside Driver</div>
                <div className="font-display text-2xl uppercase text-asap-deep" data-testid="card-name">{card.name}</div>
                <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-asap-deep text-asap-yellow text-[10px] uppercase tracking-widest font-bold">
                  <Trophy size={10}/> {card.tier.name} · {card.tier.multiplier.toFixed(2)}×
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {card.bio && <p className="text-slate-700">{card.bio}</p>}

            <div className="grid grid-cols-2 gap-3">
              <Stat label="Completed jobs" value={card.completed_jobs}/>
              <Stat label="Rating" value={`${card.rating.toFixed(1)} ★`}/>
            </div>

            <div className="space-y-2">
              {card.phone && (
                <a href={`tel:${card.phone}`} className="flex items-center gap-3 p-3 border-2 border-asap-deep hover:bg-asap-yellow/10" data-testid="card-call">
                  <Phone size={18} className="text-asap-orange"/> <span className="font-bold text-asap-deep">{card.phone}</span>
                </a>
              )}
              <a href={`mailto:${card.email}`} className="flex items-center gap-3 p-3 border-2 border-asap-deep hover:bg-asap-yellow/10">
                <Mail size={18} className="text-asap-orange"/> <span className="font-bold text-asap-deep">{card.email}</span>
              </a>
              {card.zone && (
                <div className="flex items-center gap-3 p-3 border-2 border-asap-deep">
                  <MapPin size={18} className="text-asap-orange"/>
                  <span className="font-bold text-asap-deep">Serves {card.zone.name} ({card.zone.radius_miles}mi)</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 pt-2 border-t border-slate-200">
              <img src={qrSrc} alt="QR" className="w-24 h-24 border-2 border-asap-deep"/>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest font-bold text-asap-deep">Scan to share</div>
                <p className="text-xs text-slate-500 mt-1">Save this card or send the link to anyone who needs roadside help.</p>
                <Link to="/register" data-testid="card-book-cta" className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-asap-yellow text-asap-deep font-display uppercase text-sm border-2 border-asap-deep shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all">
                  Book me <ExternalLink size={12}/>
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-asap-deep text-white/60 text-[10px] uppercase tracking-[0.3em] text-center py-2">
            ASAP Our's 24-7 Network Services · 831-747-7141
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-asap-yellow/80 underline text-xs uppercase tracking-widest font-bold">Visit ASAP Roadside</Link>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-2 border-asap-deep p-3 text-center">
      <div className="text-[10px] uppercase tracking-widest font-bold text-asap-orange">{label}</div>
      <div className="font-display text-2xl text-asap-deep">{value}</div>
    </div>
  );
}
